import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  type ComposerAttachment,
  type ComposerPanelKind,
  type FileAttachment,
  type ImageAttachment,
  type RouterAction,
  type TaskDoorControl,
  type TaskDoorRoute,
} from "@bash/shared";
import { converseStream } from "@/lib/composer/converse-client";
import { routeTaskDoor } from "@/lib/composer/route-client";
import { useModelStore, resolveComposerModel } from "@/stores/modelStore";
import { nextTaskEngine } from "@/stores/enginePreferenceStore";
import { useTaskStore } from "@/stores/taskStore";
import { dropPanel, reduceEvent, type ConverseUiState } from "./useConverse";
import { seedComposerTask } from "@/stores/composerSeed";
import { performRouterAction } from "./routerActions";
import { useConverseAgent } from "./useConverseAgent";
import type { ConverseAgentFrame, LastAsk } from "./useConverseAgent";
import { isTaskDoorEnabled } from "./task-door-flags";
import { countSubmitted, settled, type LineDoor } from "./converse-lane";
import {
  NOT_ROUTED,
  chainDoorStep,
  handBackIfTaskChanged,
  isPlainSentence,
  runDoorStep,
  type DoorSend,
} from "./task-door-chain";

/** The control receipt's beat on the inline card (plan R-P4): the card's own NAMI_AUTODISMISS_MS beat, then idle. */
export const CONTROL_RECEIPT_MS = 2200;

export interface UseComposerConverseArgs {
  /**
   * The classic Socket.IO send — the flag-off delegate AND the
   * never-lose-a-message fallback. Each call site supplies its own (the Dock's
   * also navigates to /task, matching its classic behavior exactly).
   */
  classicSend: (
    text: string,
    images?: ImageAttachment[],
    files?: FileAttachment[]
  ) => void;
  /** Extra per-send eligibility beyond the flag (the task layout passes "no task selected"). */
  isEligible?: () => boolean;
  /** The Line: when it returns a generator the ask rides the open voice line
   *  (spoken and shown) instead of the SSE lane; null = the line is not open.
   *  The signal is the send's own: a dismiss or a newer send aborts it. */
  viaLine?: LineDoor;
  /**
   * The task-screen door (spec 2026-09-20 §3.2, R-U4): the host runs a control
   * the server named (open or close the computer, stop) with its own toolbar's
   * handlers, and says whether it did (R-L22: a stop with nothing in flight is
   * not handled, and the words ride the task as a message). The door opens only
   * on a host that passes this: with the web flag on, a plain sentence typed on
   * /task/<id> asks POST /api/composer/route which rail it rides.
   */
  onControl?: (control: TaskDoorControl, taskId: string) => boolean;
}

export interface UseComposerConverseResult extends ConverseAgentFrame {
  /** Inline card state (ConverseUiState reused verbatim; 'idle' = render nothing). */
  ui: ConverseUiState;
  /** The ask being answered inline (for the "You" line). */
  question: string;
  submit: (
    text: string,
    images?: ImageAttachment[],
    files?: FileAttachment[]
  ) => void;
  /** Error-state retry: re-send the failed ask via the CLASSIC path (never re-streams). */
  retryClassic: () => void;
  dismiss: () => void;
  /** The × on one panel (composer panels, spec 2026-09-19 §3.3): the answer stands, the panel goes. */
  closePanel: (kind: ComposerPanelKind) => void;
}

/**
 * useComposerConverse — routes typed new-task asks through the composer
 * gateway (`surface: 'composer'`) so 1–2-step things answer inline (~1s) and
 * harder asks come back as a `task_created` action that navigates into the
 * classic thread.
 *
 * Fallback contract (binding — a typed message must NEVER be lost):
 * - Ineligible (mid-thread) → `classicSend` directly, byte-identical
 *   to today. Attachments no longer force this path: the composer can see them
 *   now, so whether a photo deserves a task is ITS judgement, not a hardcoded rule.
 * - Transport failure before ANY server event (fetch rejection, non-OK,
 *   unauthorized, empty stream) → silent `classicSend` fallback, exactly once,
 *   attachments included (nothing reached the server, so nothing may vanish).
 * - `error` event after streaming began → error state; the single retry button
 *   re-sends via the CLASSIC path. Never an automatic double-send.
 * - A second send aborts the in-flight stream (fully ignored: no fallback, no
 *   state writes) and names it (`replaces`): the server answers both as ONE ask
 *   when that one ended unanswered before work. A dismiss names nothing.
 * - The Line (`viaLine`): while the voice line is open, a words-only ask
 *   rides it instead of the SSE lane: same reduction, same history writes
 *   (unmarked: a typed ask is typed, whatever carries it, and the server
 *   writes no `via` for it either). The line OWNS that send, so no classic
 *   fallback ever fires for it: an error renders as an error, and an empty
 *   stream means the answer was spoken.
 */

/** Exact decoded byte length of a base64 string (browser `atob`, real bytes —
 *  never an estimate). `0` on malformed input rather than throwing out of a send. */
function base64ByteLength(base64: string): number {
  try {
    return atob(base64).length;
  } catch {
    return 0;
  }
}

/**
 * Web attachments → the composer wire shape. A web inline image has not touched
 * the upload door YET, so it rides as pixels with no fileId, and the server's
 * converse door stores it and mints the real id before the turn runs. A web
 * FILE is already stored (uploaded on its way here, or shared with them), so it
 * rides by its /v door, and the door reads the id from that only after the gate
 * says the caller may read the file (ComposerAttachment.storageUrl). The id this used to
 * send was never taken (a client id is an IDOR), so the file reached the task
 * as a name only (#bugs 2026-09-24).
 */
export function toComposerAttachments(
  images: ImageAttachment[] | undefined,
  files: FileAttachment[] | undefined
): ComposerAttachment[] | undefined {
  const out: ComposerAttachment[] = (images ?? []).map((img, i) => ({
    filename: img.filename ?? `image-${i + 1}.${img.mediaType.split("/")[1]}`,
    mediaType: img.mediaType,
    sizeBytes: base64ByteLength(img.data),
    vision: { mediaType: img.mediaType, data: img.data },
  }));
  for (const f of files ?? []) {
    out.push({
      filename: f.filename,
      mediaType: f.mediaType,
      sizeBytes: f.sizeBytes,
      storageUrl: f.url,
    });
  }
  return out.length ? out : undefined;
}

/** Whether the person is on the task screen: the only place `selectedTaskId` is what they are looking at. */
const onTaskRoute = (): boolean =>
  typeof window !== "undefined" &&
  /^\/task(\/|$)/.test(window.location.pathname);

export function useComposerConverse(
  args: UseComposerConverseArgs
): UseComposerConverseResult {
  const router = useRouter();
  const [ui, setUi] = useState<ConverseUiState>({ phase: "idle" });
  const [question, setQuestion] = useState("");
  const { frame, beginSend, beginTurn } = useConverseAgent();
  const acRef = useRef<AbortController | null>(null);
  const lastSendRef = useRef<{ id: string; agent?: string } | null>(null); // what the next send names
  const threadIdRef = useRef<string | undefined>(undefined);
  const lastAskRef = useRef<LastAsk>({ text: "" });
  const argsRef = useRef(args);
  argsRef.current = args;
  /** The door's verdicts, acted on in the order typed (plan R-P2). */
  const doorChainRef = useRef<Promise<void>>(Promise.resolve());
  /** The control receipt's beat; cleared by a newer send, a dismiss or an unmount. */
  const receiptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearReceipt = useCallback((): void => {
    if (receiptTimerRef.current) {
      clearTimeout(receiptTimerRef.current);
      receiptTimerRef.current = null;
    }
  }, []);
  /** Set by the unmount cleanup (review T7 #6): a chain step that lands after the person left paints no card and starts no inline answer; the words ride the task. */
  const unmountedRef = useRef(false);

  // Abort any live stream if the host component unmounts.
  useEffect(() => {
    unmountedRef.current = false; // a StrictMode remount runs the cleanup once, then mounts again
    return () => {
      unmountedRef.current = true;
      clearReceipt();
      acRef.current?.abort();
      acRef.current = null;
    };
  }, [clearReceipt]);

  const dismiss = useCallback((): void => {
    clearReceipt();
    acRef.current?.abort();
    acRef.current = null;
    lastSendRef.current = null; // walked away: nothing answers it
    setUi({ phase: "idle" });
  }, [clearReceipt]);

  const closePanel = useCallback((kind: ComposerPanelKind): void => {
    setUi((u) => dropPanel(u, kind));
  }, []);

  /** Returns true when the action is handled by NAVIGATION (inline card closes). The belt lives in routerActions.ts, shared with the Line's consumer (spec 2026-09-17 §5.4). */
  const navigateForAction = useCallback(
    (action: RouterAction): boolean => performRouterAction(action, router),
    [router]
  );

  const run = useCallback(
    async (
      text: string,
      images: ImageAttachment[] | undefined,
      files: FileAttachment[] | undefined,
      ac: AbortController,
      /** The task a door-routed ask was typed on (review T7 R2): its never-lose fallbacks hand the words back rather than land them on a task the person has since picked. */
      doorTaskId?: string,
      /** Agents first (train 1b): the agent this run is FOR; see beginTurn. */
      forAgent?: string,
      /** The request id of the send this one cancelled (see the contract above). */
      replaces?: string
    ): Promise<void> => {
      // One id per exchange (2026-09-28): the request's id names the mirror row
      // too, and the server stores it as the row's own, so the two are one row.
      const id = crypto.randomUUID();
      lastSendRef.current = { id, agent: forAgent };
      const turn = beginTurn(forAgent, threadIdRef, text.trim(), id);
      const req = {
        input: text.trim(),
        attachments: toComposerAttachments(images, files),
        surface: "composer" as const,
        threadId: threadIdRef.current,
        agent: turn.agent,
        idempotencyKey: id,
        ...(replaces ? { replaces } : {}),
        // The pill's pick rides along so a spawned task runs on it (founder
        // ruling 07-16: explicit pick beats the server's model-policy ladder).
        // ONLY a real pick rides (founder 08-25, the Luna-echo incident): an
        // untouched pill sends nothing, so the server's account-preference →
        // runtime_config default chain decides. Read at send time.
        // On Claude Code (engines T2b) only a pick that engine can run rides.
        taskModel: resolveComposerModel({
          ...useModelStore.getState(),
          runtime: "native",
          engine: nextTaskEngine(),
        }).model,
        // The situation (spec 2026-09-17 §5.3): the open task rides only on a task
        // route (R-P-8: a selectedTaskId outlives the page it was picked on, and
        // a stale one would make the situation line say "viewing the task X" on
        // the routines page); converse-client merges tz and the view.
        context:
          onTaskRoute() && useTaskStore.getState().selectedTaskId
            ? {
                activeTaskId: useTaskStore.getState().selectedTaskId as string,
              }
            : undefined,
      };
      // The Line's typed door: words only (the line carries no attachments),
      // never for an agent (rule D, useConverseAgent), and only while it is
      // open. The line owns the sends it does take, so the never-lose
      // fallbacks below belong to the SSE lane alone.
      const viaLine =
        images?.length || files?.length || turn.agent
          ? null
          : (argsRef.current.viaLine?.(text.trim(), ac.signal, req) ?? null);
      const record = turn.record;
      // Analytics v1, once per ask (converse-lane says when each lane counts).
      const submitted = countSubmitted(req.surface, text, doorTaskId, viaLine);
      let acc: ConverseUiState = { phase: "thinking" };
      let started = false; // any server event arrived (incl. thread) — after this, never auto-resend
      const source = viaLine
        ? settled(viaLine)
        : converseStream(req, { signal: ac.signal, stream: true });
      for await (const ev of source) {
        if (ac.signal.aborted) return; // superseded by a newer send or dismissed — drop everything
        if (ev.type === "error" && !started && !viaLine && !turn.agent) {
          // Transport failure before the stream began (fetch rejection, non-OK,
          // unauthorized): the ask silently rides the classic lane instead.
          // Nothing ran server-side yet, so this cannot double-execute — and
          // nothing reached the server, so the attachments ride along too.
          setUi({ phase: "idle" });
          if (doorTaskId && handBackIfTaskChanged(doorTaskId, text)) return;
          argsRef.current.classicSend(text, images, files);
          return;
        }
        if (!started && !viaLine) submitted();
        started = true;
        if (ev.type === "thread") {
          threadIdRef.current = ev.threadId;
          turn.readFrame(ev);
          continue;
        }
        if (ev.type === "action") {
          // Settled outcomes land in the composer history (double-click on the
          // pill) — the inline card is ephemeral, the transcript is not.
          const a = ev.action;
          if (a.kind === "task_created") {
            // Entry-race fix: the receipt is post-persist truth (task row + ask
            // row exist server-side) — seed the store NOW so stepping into the
            // task paints title, model, and the ask on the first frame instead
            // of a blank shell waiting on the MESSAGE_SYNC round trip.
            seedComposerTask(a);
          }
          if (a.kind === "task_created" || a.kind === "task_followup") {
            record({
              kind: a.kind,
              text: a.title,
              taskId: a.taskId,
            });
          } else if (a.kind === "nami_saved") {
            record({
              kind: "nami_saved",
              text: a.summary ?? "",
            });
          }
          if (navigateForAction(a)) {
            ac.abort(); // stop reading the rest of the body
            setUi({ phase: "idle" });
            return;
          }
        }
        acc = reduceEvent(acc, ev);
        setUi(acc);
      }
      if (!started && !ac.signal.aborted) {
        // The stream closed without a single event. For the LEAD that is the
        // never-lose fallback it always was; on the line the ask was heard and
        // the answer is spoken, so the card simply goes. An AGENT never rides
        // the classic lane (rule D), so its ask lands on the error card.
        if (turn.agent) {
          setUi({ phase: "error", message: "That did not send. Try again." });
          return;
        }
        setUi({ phase: "idle" });
        if (!viaLine) {
          if (doorTaskId && handBackIfTaskChanged(doorTaskId, text)) return;
          argsRef.current.classicSend(text, images, files);
        }
        return;
      }
      if (
        !ac.signal.aborted &&
        (acc.phase === "answered" || acc.phase === "error")
      ) {
        record(
          acc.phase === "answered"
            ? { kind: "answered", text: acc.text }
            : { kind: "error", text: acc.message }
        );
      }
    },
    [beginTurn, navigateForAction]
  );

  /**
   * The task-screen door (spec 2026-09-20 §3.2; plan R-P2): the route is asked
   * at submit, and verdicts are ACTED ON in the order typed (a chain). The step
   * itself is task-door-chain's runDoorStep (R-L9); the hook lends it the refs
   * and the UI moves it owns. A door send is never dropped and a step never
   * rejects, so one bad handler never sticks the chain.
   */
  const door = useCallback(
    (send: DoorSend, verdict: TaskDoorRoute | Promise<TaskDoorRoute>): void => {
      const trimmed = send.text.trim();
      doorChainRef.current = chainDoorStep(doorChainRef.current, async () => {
        const v = await verdict;
        const a = argsRef.current;
        runDoorStep(v, send, {
          classicSend: a.classicSend,
          onControl: a.onControl,
          live: !unmountedRef.current,
          showReceipt: (words) => {
            clearReceipt();
            // A control supersedes an inline answer still streaming (round 2),
            // or the stream would paint over the receipt.
            acRef.current?.abort();
            acRef.current = null;
            lastSendRef.current = null; // a control ended it: nothing answers it
            setQuestion(trimmed);
            setUi({ phase: "answered", text: words });
            receiptTimerRef.current = setTimeout(() => {
              receiptTimerRef.current = null;
              // Only its own receipt (review T7 #2): a later answer stands.
              setUi((u) =>
                u.phase === "answered" && u.text === words
                  ? { phase: "idle" }
                  : u
              );
            }, CONTROL_RECEIPT_MS);
          },
          startAnswer: () => {
            clearReceipt();
            acRef.current?.abort();
            const ac = new AbortController();
            acRef.current = ac;
            setQuestion(trimmed);
            setUi({ phase: "thinking" });
            const { text, images, files, taskId, replaces } = send;
            void run(text, images, files, ac, taskId, undefined, replaces);
          },
          idle: () => {
            clearReceipt();
            setUi({ phase: "idle" });
          },
        });
      });
    },
    [clearReceipt, run]
  );

  const submit = useCallback(
    (
      text: string,
      images?: ImageAttachment[],
      files?: FileAttachment[]
    ): void => {
      const a = argsRef.current;
      // A newer send supersedes any in-flight inline answer (simplest correct concurrency).
      const last = lastSendRef.current;
      lastSendRef.current = null;
      acRef.current?.abort();
      acRef.current = null;
      const trimmed = text.trim();
      const hasAttachments = Boolean(images?.length || files?.length);
      clearReceipt();
      // The task-screen door (spec 2026-09-20 §3.2): on a task's own screen, with
      // the web flag on and a host that can run a control, a plain sentence asks
      // the server which rail it rides. Any other send on that host rides the
      // task in its turn on the chain (below); with the converse flag off (the
      // early return above) every send is untouched, door flag or not.
      const doorTaskId =
        isTaskDoorEnabled() && a.onControl && onTaskRoute()
          ? useTaskStore.getState().selectedTaskId
          : null;
      if (doorTaskId && isPlainSentence(trimmed, hasAttachments)) {
        lastAskRef.current = { text, images, files };
        setUi({ phase: "idle" });
        door(
          { taskId: doorTaskId, text, images, files, replaces: last?.id },
          routeTaskDoor({ taskId: doorTaskId, text: trimmed })
        );
        return;
      }
      // Mid-thread still delegates. Attachments no longer do: the composer can
      // see them now, and whether a photo deserves a task is ITS judgement.
      if ((a.isEligible && !a.isEligible()) || (!trimmed && !hasAttachments)) {
        setUi({ phase: "idle" });
        if (doorTaskId) {
          // A send the door does not route, typed while an earlier door send may
          // still await its verdict (review T7 #5): it takes its place on the
          // chain, so the task hears everything in the order typed, same guard.
          door({ taskId: doorTaskId, text, images, files }, NOT_ROUTED);
          return;
        }
        a.classicSend(text, images, files);
        return;
      }
      const ac = new AbortController();
      acRef.current = ac;
      const forAgent = beginSend();
      // Only a send to the same one: another agent's words belong to its own thread.
      const replaces = last && last.agent === forAgent ? last.id : undefined;
      lastAskRef.current = { text, images, files, agent: forAgent };
      setQuestion(trimmed);
      setUi({ phase: "thinking" });
      void run(text, images, files, ac, undefined, forAgent, replaces);
    },
    [beginSend, clearReceipt, door, run]
  );

  const retryClassic = useCallback((): void => {
    // THE WHOLE ASK, not just its words. This used to re-send text only, on the
    // reasoning that the attachment had already reached the server — true, but
    // it made Retry a no-op for a PHOTO-ONLY send, which has no words: the error
    // cleared, nothing was sent, and the user was left looking at an idle
    // composer that had silently eaten their picture. A send is retryable when
    // it had words OR files; anything else has nothing to retry and must not
    // clear the error as though it did.
    const ask = lastAskRef.current;
    const hasAttachments = Boolean(ask.images?.length || ask.files?.length);
    if (!ask.text && !hasAttachments) return;
    // An agent's ask never rides the classic lane, and its retry re-runs on
    // the agent it was made TO (rule D, useConverseAgent).
    if (ask.agent) {
      acRef.current?.abort();
      const ac = new AbortController();
      acRef.current = ac;
      setUi({ phase: "thinking" });
      void run(ask.text, ask.images, ask.files, ac, undefined, ask.agent);
      return;
    }
    setUi({ phase: "idle" });
    argsRef.current.classicSend(ask.text, ask.images, ask.files);
  }, [run]);

  return { ui, question, ...frame, submit, retryClassic, dismiss, closePanel };
}
